import React, { useEffect, useRef, useState } from 'react'
import { Box, Text, VStack, HStack, Progress, Button, IconButton, Image, Badge } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { RepeatIcon } from '@chakra-ui/icons'

const MotionBox = motion(Box)
const MotionText = motion(Text)
const MotionImage = motion(Image)

type VisualMode = 'matrix' | 'network' | 'cyber' | 'robot'

const AiWorkingOverlay: React.FC = () => {
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [log, setLog] = useState<string>('Initializing AI Agent...')
  const [mode, setMode] = useState<VisualMode>('network')
  const [profit, setProfit] = useState<number>(1240.50)
  const [showWin, setShowWin] = useState<boolean>(false)

  // Canvas Animation Logic
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resize = () => {
      canvas.width = canvas.parentElement?.clientWidth || 300
      canvas.height = canvas.parentElement?.clientHeight || 200
    }
    resize()
    window.addEventListener('resize', resize)

    let animationFrameId: number
    let particles: any[] = []

    // Initialize Particles for Network Mode
    if (mode === 'network') {
      for (let i = 0; i < 60; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 1.5,
          vy: (Math.random() - 0.5) * 1.5,
          size: Math.random() * 2 + 1
        })
      }
    }

    // Initialize Columns for Matrix Mode
    const columns = Math.floor(canvas.width / 20)
    const drops: number[] = []
    for (let i = 0; i < columns; i++) drops[i] = 1

    // Cyber HUD Variables
    let angle = 0

    const render = () => {
      // Clear Canvas
      ctx.fillStyle = mode === 'matrix' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.2)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      if (mode === 'matrix') {
        ctx.fillStyle = '#0F0'
        ctx.font = '15px monospace'
        for (let i = 0; i < drops.length; i++) {
          const text = String.fromCharCode(Math.floor(Math.random() * 128))
          ctx.fillText(text, i * 20, drops[i] * 20)
          if (drops[i] * 20 > canvas.height && Math.random() > 0.975) drops[i] = 0
          drops[i]++
        }
      } else if (mode === 'network') {
        ctx.fillStyle = '#00BFFF' // Deep Sky Blue
        ctx.strokeStyle = '#00BFFF'
        
        // Update and Draw Particles
        particles.forEach((p, i) => {
          p.x += p.vx
          p.y += p.vy

          // Bounce off walls
          if (p.x < 0 || p.x > canvas.width) p.vx *= -1
          if (p.y < 0 || p.y > canvas.height) p.vy *= -1

          ctx.beginPath()
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
          ctx.fill()

          // Draw Connections
          for (let j = i + 1; j < particles.length; j++) {
            const p2 = particles[j]
            const dx = p.x - p2.x
            const dy = p.y - p2.y
            const dist = Math.sqrt(dx * dx + dy * dy)

            if (dist < 100) {
              ctx.globalAlpha = 1 - dist / 100
              ctx.lineWidth = 0.5
              ctx.beginPath()
              ctx.moveTo(p.x, p.y)
              ctx.lineTo(p2.x, p2.y)
              ctx.stroke()
              ctx.globalAlpha = 1
            }
          }
        })
      } else if (mode === 'cyber') {
        const cx = canvas.width / 2
        const cy = canvas.height / 2
        angle += 0.02

        ctx.strokeStyle = '#E91E63' // Pink/Red
        ctx.lineWidth = 2

        // Ring 1
        ctx.beginPath()
        ctx.arc(cx, cy, 50, angle, angle + Math.PI * 1.5)
        ctx.stroke()

        // Ring 2
        ctx.strokeStyle = '#9C27B0' // Purple
        ctx.beginPath()
        ctx.arc(cx, cy, 70, -angle * 1.5, -angle * 1.5 + Math.PI)
        ctx.stroke()

        // Ring 3
        ctx.strokeStyle = '#00BCD4' // Cyan
        ctx.beginPath()
        ctx.arc(cx, cy, 90, angle * 0.5, angle * 0.5 + Math.PI * 0.5)
        ctx.stroke()
        
        // Scanning Line
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)'
        ctx.beginPath()
        ctx.moveTo(0, (Math.sin(angle) + 1) * canvas.height / 2)
        ctx.lineTo(canvas.width, (Math.sin(angle) + 1) * canvas.height / 2)
        ctx.stroke()
      } else if (mode === 'robot') {
        // Robot mode: simple grid background
        ctx.strokeStyle = 'rgba(0, 255, 255, 0.1)'
        ctx.lineWidth = 1
        
        const gridSize = 30
        const offset = (Date.now() / 50) % gridSize
        
        for (let x = 0; x < canvas.width; x += gridSize) {
          ctx.beginPath()
          ctx.moveTo(x, 0)
          ctx.lineTo(x, canvas.height)
          ctx.stroke()
        }
        for (let y = offset; y < canvas.height; y += gridSize) {
          ctx.beginPath()
          ctx.moveTo(0, y)
          ctx.lineTo(canvas.width, y)
          ctx.stroke()
        }
      }

      animationFrameId = requestAnimationFrame(render)
    }

    render()

    return () => {
      window.removeEventListener('resize', resize)
      cancelAnimationFrame(animationFrameId)
    }
  }, [mode])

  // Random Log Messages & Profit Simulation
  useEffect(() => {
    const logs = [
      'Analyzing market depth...',
      'Calculating RSI divergence...',
      'Checking order book imbalance...',
      'Verifying support levels...',
      'Optimizing entry point...',
      'Monitoring volatility...',
      'AI Neural Net: Confidence 87%...',
      'Executing strategy: Scalping...',
      'Fetching real-time tick data...',
      'Target Profit Reached! Closing position...',
      'High probability setup detected...',
      'Accumulating assets...',
      'Trend confirmation: BULLISH'
    ]
    
    const logInterval = setInterval(() => {
      const newLog = logs[Math.floor(Math.random() * logs.length)]
      setLog(newLog)
      
      // Simulate "Win" event occasionally
      if (newLog.includes('Profit') || newLog.includes('BULLISH')) {
        setShowWin(true)
        setTimeout(() => setShowWin(false), 2000)
        setProfit(prev => prev + (Math.random() * 10 + 5))
      }
    }, 2000)

    // Constant small profit tick
    const profitInterval = setInterval(() => {
        setProfit(prev => prev + (Math.random() * 0.5))
    }, 500)

    return () => {
        clearInterval(logInterval)
        clearInterval(profitInterval)
    }
  }, [])

  const toggleMode = () => {
    if (mode === 'matrix') setMode('network')
    else if (mode === 'network') setMode('cyber')
    else if (mode === 'cyber') setMode('robot')
    else setMode('matrix')
  }

  const getThemeColor = () => {
    if (mode === 'matrix') return 'green'
    if (mode === 'network') return 'cyan'
    if (mode === 'cyber') return 'pink'
    return 'blue' // Robot
  }

  const themeColor = getThemeColor()

  return (
    <Box
      position="relative"
      w="full"
      h="300px"
      bg="black"
      borderRadius="2xl"
      overflow="hidden"
      border="1px solid"
      borderColor={`${themeColor}.500`}
      boxShadow={`0 0 20px rgba(${mode === 'matrix' ? '0, 255, 0' : mode === 'network' ? '0, 191, 255' : mode === 'cyber' ? '233, 30, 99' : '0, 100, 255'}, 0.2)`}
    >
      {/* Canvas Background */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.6
        }}
      />

      {/* Overlay Content */}
      <VStack
        position="absolute"
        top={0}
        left={0}
        w="full"
        h="full"
        justify="center"
        align="center"
        spacing={6}
        zIndex={1}
        pointerEvents="none"
      >
        {mode === 'robot' ? (
          <Box position="relative">
             <MotionImage
              src="/images/ai_robot_head.png"
              w="120px"
              h="120px"
              objectFit="contain"
              animate={{
                y: [0, -10, 0],
                filter: [
                  'drop-shadow(0 0 10px rgba(0, 200, 255, 0.5))',
                  'drop-shadow(0 0 20px rgba(0, 200, 255, 0.8))',
                  'drop-shadow(0 0 10px rgba(0, 200, 255, 0.5))'
                ]
              }}
              transition={{
                y: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                filter: { duration: 2, repeat: Infinity, ease: "easeInOut" }
              }}
            />
            {/* Scanning Line Effect */}
            <MotionBox
              position="absolute"
              top={0}
              left={0}
              w="100%"
              h="2px"
              bg="cyan.400"
              boxShadow="0 0 10px cyan"
              animate={{ top: ['0%', '100%', '0%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              opacity={0.7}
            />
          </Box>
        ) : (
          <Box position="relative">
            <MotionBox
              w="80px"
              h="80px"
              borderRadius="full"
              border="4px solid"
              borderColor={`${themeColor}.400`}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [1, 0.5, 1],
                boxShadow: [
                  `0 0 0px ${themeColor}`,
                  `0 0 20px ${themeColor}`,
                  `0 0 0px ${themeColor}`
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              color="white"
              fontWeight="bold"
              fontSize="xl"
            >
              AI
            </Box>
          </Box>
        )}

        <VStack spacing={2}>
          <HStack>
              <Text
                color={`${themeColor}.400`}
                fontSize="2xl"
                fontWeight="bold"
                letterSpacing="wider"
                textShadow={`0 0 10px ${themeColor}`}
            >
                {t('AI AGENT WORKING')}
            </Text>
            {/* {showWin && (
                <MotionBox
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1.2, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                >
                    <Badge colorScheme="green" fontSize="lg" variant="solid">WIN!</Badge>
                </MotionBox>
            )} */}
          </HStack>
          
          {/* Live Profit Counter */}
          {/* <Text color="green.300" fontSize="lg" fontWeight="bold" fontFamily="monospace">
            PNL: +${profit.toFixed(2)}
          </Text> */}

          <AnimatePresence mode="wait">
            <MotionText
              key={log}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              color="gray.300"
              fontFamily="monospace"
            >
              {`> ${log}`}
            </MotionText>
          </AnimatePresence>
        </VStack>

        {/* Progress Bar */}
        <Box w="80%">
            <Progress
                size="xs"
                isIndeterminate
                colorScheme={themeColor}
                bg="gray.800"
                borderRadius="full"
            />
        </Box>
      </VStack>

      {/* Mode Toggle Button */}
      <IconButton
        aria-label="Change Theme"
        icon={<RepeatIcon />}
        position="absolute"
        top={4}
        right={4}
        size="sm"
        colorScheme={themeColor}
        variant="outline"
        onClick={toggleMode}
        zIndex={2}
        pointerEvents="auto"
        _hover={{ bg: 'whiteAlpha.200' }}
      />
    </Box>
  )
}

export default AiWorkingOverlay
